import { AfterViewInit, ChangeDetectorRef, Directive, ElementRef, Input, NgZone, OnDestroy } from "@angular/core";

@Directive({
  standalone: false,
  selector: "[uiOverflowTooltip]",
  exportAs: "uiOverflowTooltip"
})
export class OverflowTooltipDirective implements AfterViewInit, OnDestroy {
  @Input("uiOverflowTooltip") targetSelector = "";

  public isOverflowing = false;

  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly cdr: ChangeDetectorRef,
    private readonly zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    // Defer the first measurement past the current change-detection cycle so the
    // tooltip's `showTooltip` binding doesn't trip ExpressionChangedAfterChecked.
    Promise.resolve().then(() => this.update());

    this.zone.runOutsideAngular(() => {
      if (typeof ResizeObserver !== "undefined") {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.observeTargets();
      }

      if (typeof MutationObserver !== "undefined") {
        this.mutationObserver = new MutationObserver(() => {
          this.observeTargets();
          this.update();
        });
        this.mutationObserver.observe(this.host.nativeElement, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  private getTargets(): HTMLElement[] {
    const host = this.host.nativeElement;
    if (!this.targetSelector) {
      return [host];
    }
    return Array.from(host.querySelectorAll<HTMLElement>(this.targetSelector));
  }

  private observeTargets(): void {
    if (!this.resizeObserver) {
      return;
    }
    this.resizeObserver.disconnect();
    for (const target of this.getTargets()) {
      this.resizeObserver.observe(target);
    }
  }

  private update(): void {
    const overflowing = this.getTargets().some(el => this.isCutOff(el));
    if (overflowing !== this.isOverflowing) {
      this.zone.run(() => {
        this.isOverflowing = overflowing;
        this.cdr.markForCheck();
      });
    }
  }

  private isCutOff(el: HTMLElement): boolean {
    if (el.scrollWidth - el.clientWidth > 1) {
      return true;
    }
    return getComputedStyle(el).getPropertyValue("-webkit-line-clamp") !== "none" && el.scrollHeight - el.clientHeight > 1;
  }
}
